import idSerializer from "geotab-uuid";
import type { IAnnotation } from "../../../../model/DutyStatusLog";

type ExemptionType = "PC" | "YM";

export interface IExemptionEditContext {
    status: ExemptionType;
    start: string;
    end: string;
    newEnd: string | null;
    editor: Pick<IUser, "id" | "name" | "firstName" | "lastName">;
    dateTime: string;
}

const utcDateTimePattern = /\b\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z\b/g;
const maxCommentLength = 500;

function withEditor(prefix: string, label: string, suffix = ""): string {
    const remaining = maxCommentLength - prefix.length - suffix.length;
    if (label.length <= remaining) {
        return `${ prefix }${ label }${ suffix }`;
    }
    const trimmed = label.slice(0, Math.max(0, remaining - 1)).replace(/[\uD800-\uDBFF]$/, "");
    return `${ prefix }${ trimmed }…${ suffix }`;
}

const exemptionEditAnnotationPattern = /^(?:PC|YM) (?:shortened|removed) by |^(?:PC|YM) (?:shorten|removal) requested by |^Created from (?:PC|YM) shorten \(|^Replaces removed (?:PC|YM) period \(/;

export type TLinkedExemptionAnnotations = Record<string, IAnnotation[]>;

// A retained PC/YM is not re-downloaded when its edit annotation is saved, so saved edit annotations are linked to it through their dutyStatusLog.
// Index them once per render instead of scanning every saved annotation for each displayed log.
export function indexLinkedExemptionAnnotations(savedAnnotations: Record<string, IAnnotation>): TLinkedExemptionAnnotations {
    const index: TLinkedExemptionAnnotations = {};
    Object.keys(savedAnnotations).forEach(key => {
        const annotation = savedAnnotations[key];
        const logId = annotation.dutyStatusLog?.id;
        if (logId && exemptionEditAnnotationPattern.test(annotation.comment ?? "")) {
            (index[logId] = index[logId] ?? []).push(annotation);
        }
    });
    return index;
}

export function getLinkedExemptionAnnotations(
    log: { id: string; parentId?: string },
    index: TLinkedExemptionAnnotations,
    listedAnnotations: object[]
): IAnnotation[] {
    const listedIds = new Set(listedAnnotations.map(annotation => (annotation as { id?: string }).id));
    const linked = [...(index[log.id] ?? []), ...(log.parentId && log.parentId !== log.id ? index[log.parentId] ?? [] : [])];
    return linked.filter(annotation => !listedIds.has(annotation.id));
}

export function formatExemptionEditAnnotation(comment: string, formatDateTime: (dateTime: string) => string): string {
    if (!exemptionEditAnnotationPattern.test(comment)) {
        return comment;
    }
    return comment.replace(utcDateTimePattern, dateTime =>
        Number.isNaN(new Date(dateTime).getTime()) ? dateTime : formatDateTime(dateTime));
}

export function createExemptionEditAnnotation(context: IExemptionEditContext, kind: "source" | "replacement"): IAnnotation {
    const { status, editor, newEnd } = context;
    const fullName = [editor.firstName, editor.lastName].filter(Boolean).join(" ");
    const editorName = (fullName && editor.name ? `${ fullName } (${ editor.name })` : fullName || editor.name) || "Unknown user";
    const end = new Date(context.end).toISOString();
    let comment: string;

    if (newEnd) {
        const changedEnd = `end changed from ${ end } to ${ new Date(newEnd).toISOString() }`;
        comment = kind === "source"
            ? withEditor(`${ status } shortened by `, editorName, `: ${ changedEnd }`)
            : withEditor(`Created from ${ status } shorten (${ changedEnd }), edited by `, editorName);
    } else {
        const removedPeriod = `from ${ new Date(context.start).toISOString() } to ${ end }`;
        comment = kind === "source"
            ? withEditor(`${ status } removed by `, editorName, `: period ${ removedPeriod }`)
            : withEditor(`Replaces removed ${ status } period (${ removedPeriod }), edited by `, editorName);
    }

    return { id: idSerializer.get(), dateTime: context.dateTime, driver: { id: editor.id }, comment };
}
